///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { Component } from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { uniqBy } from "lodash";

import messages from "../../../helpers/messages";
import AutoComplete from "../../form-controls/AutoComplete";
import OutlinedInput from "../../form-controls/OutlinedInput";
import {
  assetType,
  carrierMatchingEqTypes,
  odLoadingTypes
} from "../../../helpers/types";
import {
  createLaneMatches,
  setCarriersSelected,
  updateLaneMatches
} from "../../../slices/bidProcurementSlice";
import DeleteIcon from "../../icons/DeleteIcon";
import NoteIcon from "../../icons/NoteIcon";
import RateHistory from "./RateHistory";
import ChevronDown from "../../icons/ChevronDown";

export class SubmissionDetails extends Component {
  componentDidMount() {
    const {
      dispatch,
      bidProcurement: {
        rowsSelected,
        capacityMatchCarriers: { carriers }
      }
    } = this.props;
    dispatch(createLaneMatches({ truckloadLaneIds: rowsSelected, carriers }));
  }

  componentDidUpdate(prevProps) {
    const {
      dispatch,
      bidProcurement: {
        rowsSelected,
        capacityMatchCarriers: { carriers }
      }
    } = this.props;
    if (
      prevProps.bidProcurement.rowsSelected !== rowsSelected ||
      prevProps.bidProcurement.capacityMatchCarriers.carriers !== carriers
    ) {
      dispatch(createLaneMatches({ truckloadLaneIds: rowsSelected, carriers }));
    }
  }

  updateLaneMatch = (carrierId, field, value) => {
    const {
      dispatch,
      bidProcurement: { rowsSelected }
    } = this.props;
    dispatch(
      updateLaneMatches({
        truckloadLaneIds: rowsSelected,
        carrierId,
        field,
        value
      })
    );
  };

  handleChange = (event, carrierId) => {
    if (
      !event.target.validity.patternMismatch &&
      parseInt(event.target.value, 10) !== parseInt(event.target.min, 10)
    ) {
      this.updateLaneMatch(
        carrierId,
        event.target.id,
        event.target.value ? parseInt(event.target.value, 10) : ""
      );
    }
  };

  handleTrimText = (carrierId, field, value) => {
    if (value) {
      this.updateLaneMatch(carrierId, field, value.trim());
    } else {
      this.updateLaneMatch(carrierId, field, "");
    }
  };

  render() {
    const {
      bidProcurement: {
        rowsSelected,
        capacityMatchCarriers: { laneMatches, carriers },
        rateHistory
      },
      dispatch
    } = this.props;

    const selectedCarriers = carriers?.filter(x => x.checked === true);
    const className = rowsSelected?.length === 1 ? "one-fifth" : "one-quarter";

    return (
      <div className="mt-48 pt-12 pl-24" data-testid="submission-details">
        <span className="font-size-18 weight--semibold">
          {messages.addSubmissionDetails.defaultMessage}
        </span>
        <p className="pt-8">
          <FormattedMessage
            {...messages.addAnyAdditionalDetailsToTheMatchesThatYouSelected}
            values={{
              action: rowsSelected?.length > 1 ? "Continue" : "Finish"
            }}
          />
        </p>
        <div
          className="pr-28 submission-details"
          style={{ minHeight: "392px" }}
        >
          {uniqBy(laneMatches, c => c.carrierId)?.map((laneMatch, index) => {
            const carrierRateHistory = rateHistory?.rateHistories?.filter(x => {
              return x.carrierId === laneMatch.carrierId;
            });
            const sortedCarrierRateHistory = carrierRateHistory?.sort((a, b) => {
              return new Date(b.createDateUTC) - new Date(a.createDateUTC);
            });

            const lastRateUpdateUser = sortedCarrierRateHistory?.length > 0 ? sortedCarrierRateHistory[0].userName : null;

            return (
              <div key={index} data-testid="lane-match">
                <div className="mt-40 mb-8">
                  <span
                    className="mr-24 font-bold one-half truncate inline-block"
                    data-testid="carrier-name"
                    title={`${laneMatch.carrierName} (${laneMatch.carrierCode})`}
                  >
                    {laneMatch.carrierName} ({laneMatch.carrierCode})
                  </span>
                  <span
                    className="carriers-cell align-super"
                    style={{ display: "inline-block" }}
                    data-testid="incumbent"
                  >
                    {laneMatch.incumbent
                      ? messages.incumbent.defaultMessage
                      : null}
                  </span>
                  {selectedCarriers.length > 1 ? (
                    <div
                      className="fr text-red-failure-dark pointer"
                      onClick={() =>
                        dispatch(setCarriersSelected(laneMatch.carrierId))
                      }
                      data-testid="remove"
                    >
                      <DeleteIcon
                        height="14px"
                        width="12px"
                        fill="#c71e0c"
                        className="mr-8 align-text-bottom"
                      />
                      {messages.remove.defaultMessage}
                    </div>
                  ) : null}
                </div>
                <div className="grid section-grid">
                  <div
                    className={`grid__cell ${className}`}
                    data-testid="odLoadingTypes"
                  >
                    <AutoComplete
                      required={true}
                      label={messages.serviceType.defaultMessage}
                      staticOptions={odLoadingTypes}
                      isMulti={true}
                      value={laneMatch?.odLoadingTypes || null}
                      handleChange={odLoadingTypes =>
                        this.updateLaneMatch(
                          laneMatch.carrierId,
                          "odLoadingTypes",
                          odLoadingTypes
                        )
                      }
                    />
                  </div>
                  <div
                    className={`grid__cell ${className}`}
                    data-testid="equipmentType"
                  >
                    <AutoComplete
                      required={true}
                      label={messages.equipType.defaultMessage}
                      staticOptions={carrierMatchingEqTypes}
                      value={laneMatch?.equipmentType || null}
                      handleChange={equipmentType =>
                        this.updateLaneMatch(
                          laneMatch.carrierId,
                          "equipmentType",
                          equipmentType
                        )
                      }
                    />
                  </div>
                  <div
                    className={`grid__cell ${className}`}
                    data-testid="assetType"
                  >
                    <AutoComplete
                      required={true}
                      label={messages.assetType.defaultMessage}
                      staticOptions={assetType}
                      value={laneMatch?.assetType || null}
                      handleChange={assetType =>
                        this.updateLaneMatch(
                          laneMatch.carrierId,
                          "assetType",
                          assetType
                        )
                      }
                    />
                  </div>
                  <div
                    className={`grid__cell ${className}`}
                    data-testid="weeklyCap"
                  >
                    <OutlinedInput
                      id="weeklyCap"
                      required={true}
                      value={laneMatch?.weeklyCap || ""}
                      label={messages.weeklyCap.defaultMessage}
                      pattern="[0-9]*"
                      min="0"
                      maxLength="9"
                      onChange={event =>
                        this.handleChange(event, laneMatch.carrierId)
                      }
                    />
                  </div>
                  {rowsSelected?.length === 1 ? (
                    <div
                      className={`grid__cell ${className}`}
                      data-testid="carrierRate"
                    >
                      <OutlinedInput
                        id="carrierRate"
                        required={true}
                        type="text"
                        value={laneMatch?.carrierRate || ""}
                        label={messages.rate.defaultMessage}
                        pattern="^\d{0,8}(|\.\d{0,2})$"
                        unit="$"
                        isUnitPositionLeft={true}
                        onChange={event =>
                          !event.target.validity.patternMismatch &&
                          this.updateLaneMatch(
                            laneMatch.carrierId,
                            "carrierRate",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  ) : null}
                </div>				
                <div>
                  {laneMatch?.showNotes ? (
                    <>
                      <OutlinedInput
                        id="notes"
                        value={laneMatch?.notes}
                        label={messages.note.defaultMessage}
                        isMulti
                        dynamicHeight
                        onChange={event =>
                          this.updateLaneMatch(
                            laneMatch.carrierId,
                            "notes",
                            event.target.value
                          )
                        }
                        onBlur={event =>
                          this.handleTrimText(
                            laneMatch.carrierId,
                            "notes",
                            event.target.value
                          )
                        }
                      />
                      <DeleteIcon
                        id="deleteNotes"
                        height={14}
                        width={12}
                        className="pointer fr"
                        style={{
                          position: "relative",
                          bottom: "52px",
                          right: "16px"
                        }}
                        onClick={() => {
                          this.updateLaneMatch(
                            laneMatch.carrierId,
                            "notes",
                            ""
                          );
                          this.updateLaneMatch(
                            laneMatch.carrierId,
                            "showNotes",
                            false
                          );
                        }}
                      />
                      {lastRateUpdateUser ? (
                      <div className="mt-8">
                        <span data-testid="lastRateUpdateUser">{lastRateUpdateUser}</span>
                        <div className="fr">
                          <span className="text-cobalt-blue pointer">
                            <button
                              className="text-cobalt-blue pointer ml-8 no-focus"
                              style={{ zIndex: 0 }}
                              onClick={() =>
                                this.updateLaneMatch(
                                  laneMatch.carrierId,
                                  "showRateHistory",
                                  !laneMatch.showRateHistory
                                )
                              }
                              data-testid="rateHistoryAccordionButton"
                            >                            
                              <span className="mr-4" data-testid="rateHistoryAccordionText">
                                {laneMatch.showRateHistory ? messages.hideRateHistory.defaultMessage : messages.viewRateHistory.defaultMessage}
                              </span>
                              <span data-testid="rateHistoryAccordionArrow">
                                <ChevronDown
                                  height="7"
                                  width="12"
                                  rotateDegree={laneMatch.showRateHistory ? 180 : 0}
                                  title=""
                                  fill="#0066FA"
                                />
                              </span>
                            </button>
                          </span>
                        </div>
                      </div>
                      ) : null}
                    </>
                    )
                      :
                    (
                      <>
                      <span data-testid="lastRateUpdateUser">
                        {lastRateUpdateUser}
                      </span>
                      <div className="fr">
                        <button
                          data-testid="addNotes"
                          onClick={() =>
                            this.updateLaneMatch(
                              laneMatch.carrierId,
                              "showNotes",
                              true
                            )
                          }
                          className={lastRateUpdateUser ? "text-cobalt-blue pointer mr-40" : "text-cobalt-blue pointer"}
                        >
                          <NoteIcon
                            height={12}
                            width={12}
                            className="mr-8 align-text-top"
                          />
                          {messages.addNote.defaultMessage}
                        </button>
                        {lastRateUpdateUser ? (
                        <span className="text-cobalt-blue pointer">
                          <button
                            className="text-cobalt-blue pointer ml-8 no-focus"
                            style={{ zIndex: 0 }}
                            onClick={() =>
                              this.updateLaneMatch(
                                laneMatch.carrierId,
                                "showRateHistory",
                                !laneMatch.showRateHistory
                              )
                            }
                            data-testid="rateHistoryAccordionButton"
                          >
                            <span className="mr-4" data-testid="rateHistoryAccordionText">
                              {laneMatch.showRateHistory ? messages.hideRateHistory.defaultMessage : messages.viewRateHistory.defaultMessage}
                            </span>
                            <span data-testid="rateHistoryAccordionArrow">
                              <ChevronDown
                                height="7"
                                width="12"
                                rotateDegree={laneMatch.showRateHistory ? 180 : 0}
                                title=""
                                fill="#0066FA"
                              />
                            </span>
                          </button>
                        </span>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
                  <span data-testid="rateHistoryTable">
                    {laneMatch.showRateHistory ? (
                    <RateHistory carrierRateHistory={sortedCarrierRateHistory} />
                    ) : null}
                  </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  bidProcurement: state.bidProcurement
});

export default connect(mapStateToProps)(SubmissionDetails);
